import React, { useMemo } from 'react';

export function resolveMediaUrl(src) {
  if (!src || typeof src !== 'string') return null;
  const trimmed = src.trim();
  if (!trimmed) return null;
  if (
    trimmed.startsWith('http://')
    || trimmed.startsWith('https://')
    || trimmed.startsWith('data:')
    || trimmed.startsWith('blob:')
  ) {
    return trimmed;
  }
  if (trimmed.startsWith('/')) return trimmed;
  return `/${trimmed.replace(/^\/+/, '')}`;
}

function EmbedFrame({ src, title }) {
  return (
    <iframe
      src={src}
      title={title}
      className="absolute inset-0 h-full w-full"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
    />
  );
}

function FilePlayer({ src, title, poster, autoPlay = false }) {
  return (
    <video
      className="absolute inset-0 h-full w-full bg-black object-contain"
      src={src}
      poster={poster || undefined}
      controls
      playsInline
      autoPlay={autoPlay}
      preload="metadata"
      title={title}
    />
  );
}

function VideoPlayer({ video, autoPlay = false, className = '' }) {
  const playback = video?.playback || {};
  const kind = playback.kind || (video?.source_type === 'file' ? 'file' : 'none');
  const embedUrl = useMemo(() => {
    const raw = playback.embed_url || video?.file_url || video?.external_url;
    if (!raw) return null;
    if (kind === 'youtube' && autoPlay && raw.includes('youtube')) {
      const joiner = raw.includes('?') ? '&' : '?';
      return `${raw}${joiner}rel=0&modestbranding=1&autoplay=1`;
    }
    return resolveMediaUrl(raw);
  }, [autoPlay, kind, playback.embed_url, video?.external_url, video?.file_url]);
  const poster = resolveMediaUrl(video?.thumbnail_url || playback.thumbnail_url);

  if (!embedUrl) {
    return (
      <div className={`relative aspect-video w-full overflow-hidden rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 sm:rounded-2xl   ${className}`}>
        <div className="absolute inset-0 grid place-items-center text-sm text-secondary-400">
          {video?.title || 'Video'}
        </div>
      </div>
    );
  }

  return (
    <div className={`relative aspect-video w-full overflow-hidden rounded-xl bg-black shadow-glow sm:rounded-2xl max-h-[min(42dvh,100%)] landscape:max-sm:aspect-auto landscape:max-sm:h-[58dvh] landscape:max-sm:max-h-none sm:max-h-none ${className}`}>
      {kind === 'file' ? (
        <FilePlayer src={embedUrl} title={video?.title} poster={poster} autoPlay={autoPlay} />
      ) : (
        <EmbedFrame src={embedUrl} title={video?.title || 'Video'} />
      )}
    </div>
  );
}

export default VideoPlayer;
